import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productAPI } from '../services/api';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStockProducts: 0,
    inactiveProducts: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      const response = await productAPI.getAllProducts({ size: 1000 }); // Get all products for stats
      const products = response.data.content || [];
      
      setStats({
        totalProducts: products.length,
        lowStockProducts: products.filter(p => p.stockQuantity < 10).length,
        inactiveProducts: products.filter(p => p.isActive === false).length
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const quickActions = [
    {
      title: 'Manage Products',
      description: 'View, edit, and manage all products',
      path: '/admin/products',
      icon: '📦',
      color: '#007bff'
    },
    {
      title: 'Add New Product',
      description: 'Add a new product to the catalog',
      path: '/admin/products/create',
      icon: '➕',
      color: '#28a745'
    },
    {
      title: 'View Public Catalog',
      description: 'See how customers view products',
      path: '/products',
      icon: '👁️',
      color: '#17a2b8'
    }
  ];

  const handleActionClick = (path) => {
    navigate(path);
  };

  return (
    <div className="container">
      <div className="card">
        <h1>Admin Dashboard</h1>
        <div className="mb-4">
          <h3>Welcome, {user.firstName} {user.lastName}!</h3>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Role:</strong> Administrator</p>
        </div>
        
        {/* Product Management Section */}
        <div className="card">
          <h3>Product Management</h3>
          <p>Manage your product catalog with full CRUD operations:</p>
          
          {/* Quick Actions */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            {quickActions.map((action, index) => (
              <div 
                key={index}
                className="card"
                onClick={() => handleActionClick(action.path)}
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  borderLeft: `4px solid ${action.color}`,
                  ':hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '';
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '8px',
                  pointerEvents: 'none' // Prevent child from blocking clicks
                }}>
                  <span style={{ fontSize: '24px', pointerEvents: 'none' }}>{action.icon}</span>
                  <h4 style={{ margin: 0, color: action.color, pointerEvents: 'none' }}>{action.title}</h4>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#666', pointerEvents: 'none' }}>
                  {action.description}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Stats Section */}
        <div className="card">
          <h3>Product Statistics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <h4>Total Products</h4>
              <p style={{ fontSize: '32px', color: '#007bff', margin: '8px 0' }}>
                {isLoadingStats ? '...' : stats.totalProducts}
              </p>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                All products in catalog
              </p>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <h4>Low Stock</h4>
              <p style={{ fontSize: '32px', color: '#ffc107', margin: '8px 0' }}>
                {isLoadingStats ? '...' : stats.lowStockProducts}
              </p>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                Products with &lt; 10 stock
              </p>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <h4>Inactive Products</h4>
              <p style={{ fontSize: '32px', color: '#dc3545', margin: '8px 0' }}>
                {isLoadingStats ? '...' : stats.inactiveProducts}
              </p>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                Disabled products
              </p>
            </div>
          </div>
        </div>

        {/* Other Admin Tools */}
        <div className="card">
          <h3>Other Admin Tools</h3>
          <p>Additional administrative features:</p>
          <ul>
            <li>User Management (unlock accounts)</li>
            <li>Order Management</li>
            <li>System Reports</li>
            <li>Analytics Dashboard</li>
          </ul>
          <p><em>These features will be implemented in upcoming updates!</em></p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 